import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ActionSheetController,
  ModalController,
  PopoverController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { ExternalVendor } from '@usf/product-types';
import { ExternalVendorState } from '@usf/ngrx-product';
import { WarningPopoverComponent } from '../../../../../shared/components/popovers/warning-popover/warning-popover.component';
import { ListActions } from '../../../../../shared/constants/lists-constants';
import { ModalService } from '../../../../../shared/services/modal/modal.service';
import { ListsOverlayService } from '../../../../../lists/services/lists-overlay.service';
import { ManageVendorsModel } from '../../model/manage-vendors.model';
import { ExternalVendorService } from '../../services/external-vendor.service';
import { Button } from '@shared/models/ellipsis-option.model';

@Component({
  selector: 'app-manage-vendors-sticky-footer',
  templateUrl: './manage-vendors-sticky-footer.component.html',
  styleUrls: ['./manage-vendors-sticky-footer.component.scss'],
})
export class ManageVendorsStickyFooterComponent {
  @Input() externalVendorData: ManageVendorsModel;
  @Output() deleteEvent: EventEmitter<void> = new EventEmitter();
  readonly ListActions = ListActions;

  constructor(
    private modalController: ModalController,
    private popoverController: PopoverController,
    readonly platformService: PlatformService,
    private translateService: TranslateService,
    public externalVendorService: ExternalVendorService,
    private listsOverlayService: ListsOverlayService,
    private modalService: ModalService,
    private actionSheetController: ActionSheetController,
  ) {}

  selectAllVendors() {
    this.externalVendorService.setAllSelectedVendors(true);
  }

  deselectAllVendors() {
    this.externalVendorService.setAllSelectedVendors(false);
  }

  async deleteExternalVendors(
    dispatchAction: boolean,
    selectedVendors: ExternalVendor[],
  ) {
    if (!this.externalVendorData?.online) {
      const popover = await this.popoverController.create({
        component: WarningPopoverComponent,
        cssClass: 'warning-popover',
        mode: 'ios',
        translucent: true,
        componentProps: {
          title: this.translateService.instant('i18n.home.unableToConnect'),
          description: this.translateService.instant(
            'i18n.home.networkWarning',
          ),
        },
      });
      await popover.present();
    } else if (dispatchAction) {
      this.externalVendorService.deleteExternalVendors(selectedVendors);
    }
  }

  async openEditNonUsfVendorModal(mode: string) {
    const selectedVendor = this.getFirstAvailableVendor(
      this.externalVendorData?.vendors,
    );

    this.listsOverlayService.openCreateEditNonUsfVendorModal(
      mode,
      this.externalVendorData,
      selectedVendor,
    );
  }

  getFirstAvailableVendor(vendors: ExternalVendorState[]) {
    const selectedVendors = this.externalVendorService.getSelectedVendors(
      vendors,
    );
    return selectedVendors && selectedVendors.length > 0
      ? selectedVendors[0]
      : null;
  }

  async selectedVendorEllipsis(vendors: ExternalVendorState[]) {
    const buttons: Button[] = [];
    const selectedVendors = this.externalVendorService.getSelectedVendors(
      vendors,
    );
    if (selectedVendors.length === 1) {
      buttons.push({
        text: this.translateService.instant('i18n.vendors.editVendor'),
        handler: () => this.openEditNonUsfVendorModal(ListActions.edit),
      });
    }
    buttons.push(
      {
        text: this.translateService.instant('i18n.common.delete'),
        handler: () => this.handleDelete(),
      },
      {
        text: this.translateService.instant('i18n.common.cancel'),
        role: 'cancel',
        cssClass: 'cancel-button',
      },
    );

    const vendorOptionsActionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await vendorOptionsActionSheet.present();
  }

  handleDelete() {
    this.deleteEvent.emit();
  }
}
